import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CreditCard, Link2, Copy, CheckCircle2, PenLine, X } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Input, Select } from '../../components/ui/Form.jsx';

// =============================================================================
// TakePaymentCard — charge card / send payment link / record a payment
// =============================================================================
// Payments are recorded in booking_payments (one row per attempt) and shared
// across a multi-ride itinerary. Nothing here changes the booking's status.
//
//   Charge card     — owner only, two-step confirm before money moves
//   Send link       — Stripe Checkout link; emailed (optional) + copyable
//   Record payment  — owner only, logs money taken elsewhere (Stripe
//                     dashboard, bank transfer, cash). Charges nothing.
//
// Hidden for account-billing bookings (settled by invoice).
// =============================================================================

// Stripe Checkout links expire after at most 24 hours (Stripe's limit).
// An expired link can simply be re-sent from this box.

function gbp(n) {
    return `£${Number(n || 0).toFixed(2)}`;
}

function fmtDateTime(d) {
    if (!d) return '';
    return new Date(d).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

function copyToClipboard(text) {
    if (!text) return;
    navigator.clipboard?.writeText(text)
        .then(() => toast.success('Link copied — paste it into WhatsApp or a message'))
        .catch(() => toast.error('Could not copy'));
}

export default function TakePaymentCard({ booking }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const { isOwner } = useCurrentUser();

    const [mode, setMode] = useState(null);         // null | 'card' | 'link' | 'record' | 'method'

    const paymentsQuery = useQuery({
        queryKey: ['booking-payments', booking.id],
        queryFn: () => api.get(`/bookings/${booking.id}/payments`),
    });

    const deleteMutation = useMutation({
        mutationFn: (paymentId) => api.delete(`/bookings/${booking.id}/payments/${paymentId}`),
        onSuccess: () => {
            toast.success('Recorded payment removed');
            queryClient.invalidateQueries({ queryKey: ['booking-payments', booking.id] });
        },
        onError: (err) => toast.error(err.message || 'Could not remove'),
    });

    const data = paymentsQuery.data;
    const isAccount = booking.payment_method === 'account';
    const methodInfo = describeMethod(booking);
    const price = data?.default_amount;
    // Anything charged when the booking was created (card / link at creation)
    const paidAtCreation = booking.charged_amount != null ? Number(booking.charged_amount) : 0;
    const paidHere = Number(data?.paid_total || 0);
    const totalPaid = paidAtCreation + paidHere;
    const outstanding = price != null ? Math.max(0, Math.round((price - totalPaid) * 100) / 100) : null;
    const cards = data?.cards || [];
    const payments = data?.payments || [];

    function refresh() {
        queryClient.invalidateQueries({ queryKey: ['booking-payments', booking.id] });
    }

    const suggestedAmount = outstanding != null && outstanding > 0
        ? outstanding.toFixed(2)
        : (price != null ? Number(price).toFixed(2) : '');

    const hasOpenLink = payments.some((p) => p.method === 'link' && p.status === 'pending'
            && (!p.link_expires_at || new Date(p.link_expires_at) > new Date()))
        || (booking.payment_method === 'payment_link' && booking.status === 'pending_payment');

    const methodBar = (
        <div className="flex items-center justify-between gap-2 text-sm mb-4 pb-3 border-b border-paper-border-soft">
            <div className="min-w-0">
                <span className="text-ink-subtle">Method: </span>
                <span className="text-ink font-medium">{methodInfo.label}</span>
            </div>
            <button
                type="button"
                onClick={() => setMode('method')}
                disabled={!data}
                className="text-xs font-medium text-ink-muted hover:text-ink hover:underline underline-offset-2 flex-shrink-0 disabled:opacity-50"
            >
                Change
            </button>
        </div>
    );

    const methodModal = mode === 'method' && data && (
        <ChangeMethodModal
            booking={booking}
            current={methodInfo.key}
            currentNote={methodInfo.key === 'external' ? (booking.payment_external_note || '') : ''}
            accountEnabled={data.account_billing_enabled === true}
            hasOpenLink={hasOpenLink}
            onClose={() => setMode(null)}
            onDone={() => {
                setMode(null);
                // Booking status / method shown across the page — refresh everything.
                queryClient.invalidateQueries();
            }}
        />
    );

    if (isAccount) {
        return (
            <div className="card">
                <div className="card-header">
                    <h2 className="text-sm font-semibold text-ink">Payment</h2>
                </div>
                <div className="card-body">
                    {methodBar}
                    <p className="text-xs text-ink-subtle">Invoiced later — nothing to take here.</p>
                </div>
                {methodModal}
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Payment</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    How this booking is paid, and taking payment.
                </p>
            </div>

            <div className="card-body">
                {methodBar}
                <div className="grid grid-cols-2 gap-2 mb-4">
                    {isOwner && (
                        <Button
                            variant="secondary"
                            className="w-full justify-center"
                            onClick={() => setMode('card')}
                            disabled={!data || cards.length === 0}
                            title={cards.length === 0 ? 'Customer has no card on file' : undefined}
                        >
                            <CreditCard className="h-4 w-4" />
                            Charge card
                        </Button>
                    )}
                    <Button
                        variant="secondary"
                        className={`w-full justify-center ${isOwner ? '' : 'col-span-2'}`}
                        onClick={() => setMode('link')}
                        disabled={!data}
                    >
                        <Link2 className="h-4 w-4" />
                        Send link
                    </Button>
                    {isOwner && (
                        <Button
                            variant="secondary"
                            className="w-full justify-center col-span-2"
                            onClick={() => setMode('record')}
                            disabled={!data}
                        >
                            <PenLine className="h-4 w-4" />
                            Record payment taken elsewhere
                        </Button>
                    )}
                </div>

                {paymentsQuery.isLoading && (
                    <p className="text-sm text-ink-subtle">Loading…</p>
                )}
                {paymentsQuery.isError && (
                    <p className="text-sm text-status-danger">
                        {paymentsQuery.error?.message || 'Could not load payments'}
                    </p>
                )}

                {data && (
                    <>
                        <div className="grid grid-cols-3 gap-3">
                            <SummaryItem label="Price" value={price != null ? gbp(price) : '—'} />
                            <SummaryItem label="Paid" value={gbp(totalPaid)} />
                            <SummaryItem
                                label="Outstanding"
                                value={outstanding != null ? gbp(outstanding) : '—'}
                                highlight={outstanding === 0 && totalPaid > 0 ? 'success' : null}
                            />
                        </div>

                        {cards.length === 0 && (
                            <p className="text-xs text-ink-subtle mt-3">
                                No card on file — send a payment link. When the client pays, their card is saved for next time.
                            </p>
                        )}

                        {(paidAtCreation > 0 || payments.length > 0) && (
                            <ul className="mt-4 divide-y divide-paper-border-soft border-t border-paper-border-soft">
                                {paidAtCreation > 0 && (
                                    <li className="py-2.5 flex items-center justify-between gap-3 text-sm">
                                        <span className="text-ink-muted">Paid when booking was created</span>
                                        <span className="flex items-center gap-2">
                                            <span className="font-medium text-ink tabular-nums">{gbp(paidAtCreation)}</span>
                                            <PaymentPill status="succeeded" />
                                        </span>
                                    </li>
                                )}
                                {payments.map((p) => (
                                    <PaymentRow
                                        key={p.id}
                                        payment={p}
                                        canRemove={isOwner && p.method === 'manual'}
                                        onRemove={() => {
                                            if (window.confirm(`Remove the recorded payment of ${gbp(p.amount)}?`)) {
                                                deleteMutation.mutate(p.id);
                                            }
                                        }}
                                    />
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>

            {mode === 'card' && data && (
                <ChargeCardModal
                    bookingId={booking.id}
                    cards={cards}
                    defaultAmount={suggestedAmount}
                    onClose={() => setMode(null)}
                    onDone={() => { setMode(null); refresh(); }}
                />
            )}
            {mode === 'link' && data && (
                <SendLinkModal
                    bookingId={booking.id}
                    defaultEmail={data.booker_email || ''}
                    defaultAmount={suggestedAmount}
                    onClose={() => setMode(null)}
                    onDone={() => { setMode(null); refresh(); }}
                />
            )}
            {mode === 'record' && data && (
                <RecordPaymentModal
                    bookingId={booking.id}
                    defaultAmount={suggestedAmount}
                    onClose={() => setMode(null)}
                    onDone={() => { setMode(null); refresh(); }}
                />
            )}
            {methodModal}
        </div>
    );
}

// -----------------------------------------------------------------------------
// Payment method — label + change modal
// -----------------------------------------------------------------------------
const TAKE_LATER_NOTE = 'Payment to be taken later';

function describeMethod(b) {
    const note = (b.payment_external_note || '').trim();
    switch (b.payment_method) {
        case 'account':      return { key: 'account', label: 'Account billing' };
        case 'saved_card':   return { key: 'card', label: 'Card on file (charged at booking)' };
        case 'payment_link': return { key: 'link', label: 'Payment link (sent at booking)' };
        case 'external':
            return note === TAKE_LATER_NOTE
                ? { key: 'pay_later', label: 'Take payment later' }
                : { key: 'external', label: note ? `Other — ${note}` : 'Other' };
        default:             return { key: null, label: b.payment_method || 'Not set' };
    }
}

function ChangeMethodModal({ booking, current, currentNote, accountEnabled, hasOpenLink, onClose, onDone }) {
    const api = useApi();
    const [method, setMethod] = useState(
        current === 'account' || current === 'pay_later' || current === 'external' ? current : 'pay_later'
    );
    const [note, setNote] = useState(currentNote);

    const options = [
        {
            value: 'pay_later',
            label: 'Take payment later',
            description: 'Charge card / send link / record from this box. Booking shows Pending payment until paid.',
            disabled: false,
        },
        {
            value: 'account',
            label: 'Account billing',
            description: accountEnabled ? 'Invoiced later. Booking is confirmed.' : 'Customer is not set up for account billing',
            disabled: !accountEnabled,
        },
        {
            value: 'external',
            label: 'Other',
            description: 'Cash on the day, bank transfer, agreed separately. Booking is confirmed.',
            disabled: false,
        },
    ];

    const unchanged = method === current && (method !== 'external' || note.trim() === currentNote.trim());
    const noteMissing = method === 'external' && !note.trim();

    const mutation = useMutation({
        mutationFn: () => api.post(`/bookings/${booking.id}/payment-method`, {
            method,
            note: method === 'external' ? note.trim() : undefined,
        }),
        onSuccess: (r) => {
            const changed = (r.status_changes || []).length;
            toast.success(changed > 0
                ? 'Payment method changed — booking status updated'
                : 'Payment method changed');
            onDone();
        },
        onError: (err) => toast.error(err.message || 'Could not change payment method'),
    });

    return (
        <Modal open onClose={onClose} title="Change payment method" size="md">
            <div className="space-y-3">
                {options.map((o) => {
                    const selected = method === o.value;
                    return (
                        <button
                            key={o.value}
                            type="button"
                            disabled={o.disabled}
                            onClick={() => setMethod(o.value)}
                            className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                selected ? 'border-ink bg-paper-hover/40' : 'border-paper-border-soft hover:border-ink-faint'
                            }`}
                        >
                            <div className="text-sm font-medium text-ink">{o.label}</div>
                            <div className="text-xs text-ink-subtle mt-0.5">{o.description}</div>
                        </button>
                    );
                })}

                {method === 'external' && (
                    <Input
                        label="Payment note"
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g. Cash on the day"
                        maxLength={200}
                    />
                )}

                {hasOpenLink && method !== 'pay_later' && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        A payment link is still open on this booking. The client could still pay it —
                        let them know, or leave the method as it is.
                    </p>
                )}

                {multiRideNote(booking)}

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                    <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
                    <Button
                        variant="primary"
                        disabled={unchanged || noteMissing}
                        loading={mutation.isPending}
                        onClick={() => mutation.mutate()}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function multiRideNote(booking) {
    if (!booking.master_charged_total || !booking.booking_ref_master) return null;
    return (
        <p className="text-xs text-ink-subtle">
            Applies to every ride in itinerary {booking.booking_ref_master}.
        </p>
    );
}

function SummaryItem({ label, value, highlight }) {
    return (
        <div className="min-w-0">
            <div className="text-2xs uppercase tracking-wider font-medium text-ink-subtle">{label}</div>
            <div className={`mt-1 text-base font-semibold tabular-nums truncate ${highlight === 'success' ? 'text-emerald-700' : 'text-ink'}`}>
                {value}
            </div>
        </div>
    );
}

function PaymentRow({ payment: p, canRemove, onRemove }) {
    const linkExpired = p.method === 'link' && p.status === 'pending'
        && p.link_expires_at && new Date(p.link_expires_at) < new Date();
    const status = linkExpired ? 'expired' : p.status;

    let description;
    if (p.method === 'card') description = `Card · ${p.card_label || 'card on file'}`;
    else if (p.method === 'manual') description = `Recorded · ${p.note || 'payment taken elsewhere'}`;
    else description = `Link · ${p.sent_to_email || ''}`;

    let when = fmtDateTime(p.created_at);
    if (p.status === 'succeeded' && p.paid_at && p.method !== 'manual') when = `Paid ${fmtDateTime(p.paid_at)}`;
    else if (status === 'pending' && p.method === 'link' && p.link_expires_at) when = `Expires ${fmtDateTime(p.link_expires_at)}`;

    const showCopy = p.method === 'link' && status === 'pending' && p.payment_link_url;

    return (
        <li className="py-2.5 text-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-ink break-words">{description}</div>
                    <div className="text-xs text-ink-subtle mt-0.5">{when}</div>
                    {status === 'failed' && p.error && (
                        <div className="text-xs text-status-danger mt-0.5">{p.error}</div>
                    )}
                </div>
                <span className="flex items-center gap-2 flex-shrink-0">
                    <span className="font-medium text-ink tabular-nums">{gbp(p.amount)}</span>
                    <PaymentPill status={status} method={p.method} />
                </span>
            </div>
            {(showCopy || canRemove) && (
                <div className="mt-1.5 flex items-center gap-3">
                    {showCopy && (
                        <button
                            type="button"
                            onClick={() => copyToClipboard(p.payment_link_url)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted hover:text-ink"
                        >
                            <Copy className="h-3.5 w-3.5" />
                            Copy link
                        </button>
                    )}
                    {canRemove && (
                        <button
                            type="button"
                            onClick={onRemove}
                            className="inline-flex items-center gap-1 text-xs text-ink-subtle hover:text-status-danger"
                        >
                            <X className="h-3.5 w-3.5" />
                            Remove
                        </button>
                    )}
                </div>
            )}
        </li>
    );
}

function PaymentPill({ status, method }) {
    const map = {
        succeeded: { label: method === 'manual' ? 'Recorded' : 'Paid', cls: 'bg-emerald-100 text-emerald-800' },
        pending:   { label: method === 'link' ? 'Link sent' : 'Processing', cls: 'bg-amber-100 text-amber-800' },
        failed:    { label: 'Failed', cls: 'bg-red-100 text-red-800' },
        expired:   { label: 'Expired', cls: 'bg-paper-hover text-ink-subtle' },
        cancelled: { label: 'Cancelled', cls: 'bg-paper-hover text-ink-subtle' },
    };
    const s = map[status] || { label: status, cls: 'bg-paper-hover text-ink-subtle' };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-2xs font-semibold uppercase tracking-wider ${s.cls}`}>
            {s.label}
        </span>
    );
}

// -----------------------------------------------------------------------------
// Charge card modal
// -----------------------------------------------------------------------------
function ChargeCardModal({ bookingId, cards, defaultAmount, onClose, onDone }) {
    const api = useApi();
    const defaultCard = cards.find((c) => c.is_default) || cards[0];
    const [cardId, setCardId] = useState(defaultCard ? String(defaultCard.id) : '');
    const [amount, setAmount] = useState(defaultAmount);
    const [confirming, setConfirming] = useState(false);

    const selectedCard = cards.find((c) => String(c.id) === cardId);
    const amountNum = Number(amount);
    const amountValid = Number.isFinite(amountNum) && amountNum > 0;

    const mutation = useMutation({
        mutationFn: () => api.post(`/bookings/${bookingId}/payments/charge-card`, {
            amount: amountNum,
            card_id: cardId,
        }),
        onSuccess: (r) => {
            toast.success(r.status === 'succeeded'
                ? `Charged ${gbp(amountNum)}`
                : `Charge of ${gbp(amountNum)} is processing`);
            onDone();
        },
        onError: (err) => {
            toast.error(err.message || 'Card charge failed');
            setConfirming(false);
        },
    });

    return (
        <Modal open onClose={onClose} title="Charge card on file" size="md">
            {!confirming ? (
                <div className="space-y-4">
                    <Select label="Card" value={cardId} onChange={(e) => setCardId(e.target.value)}>
                        {cards.map((c) => (
                            <option key={c.id} value={String(c.id)}>
                                {cardLabel(c)}{c.is_default ? ' (default)' : ''}
                            </option>
                        ))}
                    </Select>
                    <Input
                        label="Amount (£, inc. VAT)"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                    />
                    <div className="flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                        <Button variant="secondary" onClick={onClose}>Cancel</Button>
                        <Button
                            variant="primary"
                            disabled={!amountValid || !selectedCard}
                            onClick={() => setConfirming(true)}
                        >
                            Continue
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    <p className="text-sm text-ink">
                        Charge <strong>{gbp(amountNum)}</strong> to <strong>{cardLabel(selectedCard)}</strong> now?
                    </p>
                    <p className="text-xs text-ink-subtle">
                        The card is charged immediately. If the bank asks the client to approve the payment,
                        the charge fails — send a payment link instead.
                    </p>
                    <div className="flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                        <Button variant="secondary" onClick={() => setConfirming(false)} disabled={mutation.isPending}>
                            Back
                        </Button>
                        <Button variant="primary" onClick={() => mutation.mutate()} loading={mutation.isPending}>
                            Charge {gbp(amountNum)}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

function cardLabel(c) {
    if (!c) return 'Card';
    const brand = c.brand ? c.brand.charAt(0).toUpperCase() + c.brand.slice(1) : 'Card';
    const exp = c.exp_month && c.exp_year ? ` · exp ${String(c.exp_month).padStart(2, '0')}/${String(c.exp_year).slice(-2)}` : '';
    return `${brand} •••• ${c.last4 || '????'}${exp}`;
}

// -----------------------------------------------------------------------------
// Send payment link modal — after creating, stays open showing the link with
// a Copy button (for WhatsApp etc.).
// -----------------------------------------------------------------------------
function SendLinkModal({ bookingId, defaultEmail, defaultAmount, onClose, onDone }) {
    const api = useApi();
    const [email, setEmail] = useState(defaultEmail);
    const [amount, setAmount] = useState(defaultAmount);
    const [sendEmail, setSendEmail] = useState(true);
    const [result, setResult] = useState(null);     // response after creation

    const amountNum = Number(amount);
    const amountValid = Number.isFinite(amountNum) && amountNum > 0;
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const mutation = useMutation({
        mutationFn: () => api.post(`/bookings/${bookingId}/payments/send-link`, {
            amount: amountNum,
            email: email.trim(),
            send_email: sendEmail,
        }),
        onSuccess: (r) => {
            setResult(r);
            if (sendEmail && r.email_sent) toast.success(`Payment link emailed to ${r.sent_to}`);
            else if (sendEmail && !r.email_sent) toast.warning('Link created but the email failed — copy it and send it yourself');
            else toast.success('Payment link created');
        },
        onError: (err) => toast.error(err.message || 'Could not create payment link'),
    });

    if (result) {
        return (
            <Modal open onClose={onDone} title="Payment link ready" size="md">
                <div className="space-y-4">
                    <p className="text-sm text-ink">
                        Link for <strong>{gbp(amountNum)}</strong>
                        {result.email_sent ? <> — emailed to <strong>{result.sent_to}</strong>.</> : '.'}
                    </p>
                    <div className="rounded-md border border-paper-border-soft bg-paper-hover/40 px-3 py-2">
                        <code className="text-xs text-ink break-all">{result.url}</code>
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                        <Button variant="secondary" onClick={onDone}>Close</Button>
                        <Button variant="primary" onClick={() => copyToClipboard(result.url)}>
                            <Copy className="h-4 w-4" />
                            Copy link
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal open onClose={onClose} title="Send payment link" size="md">
            <div className="space-y-4">
                <Input
                    label="Amount (£, inc. VAT)"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                />
                <Input
                    label="Client email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="client@example.com"
                />
                <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                    <input
                        type="checkbox"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                    />
                    Email the link to the client
                </label>
                <p className="text-xs text-ink-subtle">
                    The link is valid for 24 hours (Stripe’s limit). If it expires, just send a new one.
                </p>
                <p className="text-xs text-ink-subtle flex items-start gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    You’ll get a Copy link button next. It shows as Paid here once the client pays.
                </p>
                <div className="flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                    <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
                    <Button
                        variant="primary"
                        disabled={!amountValid || !emailValid}
                        loading={mutation.isPending}
                        onClick={() => mutation.mutate()}
                    >
                        {sendEmail ? 'Create & email' : 'Create link'} {amountValid ? gbp(amountNum) : ''}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

// -----------------------------------------------------------------------------
// Record payment modal — logs money taken outside this box. Charges nothing.
// -----------------------------------------------------------------------------
const RECORD_PRESETS = [
    'Paid via Stripe directly',
    'Bank transfer',
    'Cash',
    'Paid to chauffeur',
];

function RecordPaymentModal({ bookingId, defaultAmount, onClose, onDone }) {
    const api = useApi();
    const [amount, setAmount] = useState(defaultAmount);
    const [note, setNote] = useState(RECORD_PRESETS[0]);

    const amountNum = Number(amount);
    const amountValid = Number.isFinite(amountNum) && amountNum > 0;

    const mutation = useMutation({
        mutationFn: () => api.post(`/bookings/${bookingId}/payments/record`, {
            amount: amountNum,
            note: note.trim(),
        }),
        onSuccess: () => {
            toast.success(`Recorded ${gbp(amountNum)} as paid`);
            onDone();
        },
        onError: (err) => toast.error(err.message || 'Could not record payment'),
    });

    return (
        <Modal open onClose={onClose} title="Record payment" size="md">
            <div className="space-y-4">
                <p className="text-xs text-ink-subtle">
                    For money already received another way. Nothing is charged.
                </p>
                <Input
                    label="Amount received (£, inc. VAT)"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                />
                <div>
                    <Input
                        label="How was it paid?"
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        maxLength={120}
                    />
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {RECORD_PRESETS.map((p) => (
                            <button
                                key={p}
                                type="button"
                                onClick={() => setNote(p)}
                                className={`px-2 py-1 rounded text-xs border ${note === p ? 'border-ink text-ink' : 'border-paper-border-soft text-ink-subtle hover:text-ink'}`}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                    <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
                    <Button
                        variant="primary"
                        disabled={!amountValid || !note.trim()}
                        loading={mutation.isPending}
                        onClick={() => mutation.mutate()}
                    >
                        Record {amountValid ? gbp(amountNum) : ''}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
